import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search, X } from 'lucide-react';

export const TestPicker = ({ tests, items, setItems }) => {
  const [q, setQ] = useState('');
  const [custom, setCustom] = useState({ name: '', rate: '' });
  const chosen = new Set(items.map((i) => i.name));
  const shown = useMemo(
    () => tests.filter((t) => t.active && t.name.toLowerCase().includes(q.toLowerCase())),
    [tests, q],
  );

  const toggle = (t) => setItems(chosen.has(t.name) ? items.filter((i) => i.name !== t.name) : [...items, { name: t.name, rate: t.rate }]);
  const addCustom = () => {
    if (!custom.name.trim() || custom.rate === '') return;
    setItems([...items, { name: custom.name.trim(), rate: parseFloat(custom.rate) || 0 }]);
    setCustom({ name: '', rate: '' });
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input placeholder="Search test (CBC, RFT, LFT...)" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="lab-test-search" />
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto" data-testid="lab-test-chips">
        {shown.map((t) => (
          <button
            type="button"
            key={t.id}
            onClick={() => toggle(t)}
            data-testid={`lab-test-chip-${t.name.replace(/\W+/g, '-').toLowerCase()}`}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${chosen.has(t.name) ? 'bg-teal-600 text-white border-teal-700' : 'bg-white text-slate-700 border-slate-300 hover:bg-teal-50'}`}
          >
            {t.name} <span className="opacity-70">₹{t.rate}</span>
          </button>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input placeholder="Other test" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} className="h-8 text-sm" data-testid="lab-custom-test-name" />
        <Input placeholder="Rate" inputMode="decimal" value={custom.rate} onChange={(e) => setCustom({ ...custom, rate: e.target.value.replace(/[^0-9.]/g, '') })} className="h-8 text-sm w-24" data-testid="lab-custom-test-rate" />
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={addCustom} data-testid="lab-custom-test-add"><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export const SelectedItems = ({ items, setItems }) => (
  <div className="border rounded-lg divide-y" data-testid="lab-selected-items">
    {items.length === 0 && <p className="text-xs text-slate-400 p-3 text-center">No tests added yet</p>}
    {items.map((it, idx) => (
      <div key={`${it.name}-${idx}`} className="flex items-center gap-2 px-3 py-1.5">
        <span className="flex-1 text-sm font-semibold">{it.name}</span>
        <span className="text-xs text-slate-500">₹</span>
        <Input
          inputMode="decimal"
          value={it.rate}
          onChange={(e) => setItems(items.map((x, i) => (i === idx ? { ...x, rate: e.target.value.replace(/[^0-9.]/g, '') } : x)))}
          className="h-7 w-24 text-sm text-right"
          data-testid={`lab-item-rate-${idx}`}
        />
        <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-500 hover:bg-red-50 rounded p-1" data-testid={`lab-item-remove-${idx}`}>
          <X className="h-4 w-4" />
        </button>
      </div>
    ))}
  </div>
);
